import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  IconButton,
  List,
  ListItem,
  Divider,
  Autocomplete,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchOrganizationResLinks } from "../../../redux/actions/selectorExperienceActions";
import { ExperienceEntry } from "../../../redux/reducers/selectorExperienceReducer";
import { toast } from "react-toastify";

interface CreateSelectorExperienceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    organization_id: string;
    id: string;
    pre_selection_copy: string | null;
    selector_title: string | null;
    selector_subtitle: string | null;
    sub_client_mode_enabled: boolean;
    entries: ExperienceEntry[];
  }) => void;
  organizations: any[];
  orgsLoading: boolean;
  preselectedOrgId?: string;
}

interface ResLink {
  id: string;
  display_name?: string | null;
  behavior_type: string;
  assessment_id?: string | null;
  roleplay_id?: string | null;
}

const CreateSelectorExperienceDialog: React.FC<CreateSelectorExperienceDialogProps> = ({
  open,
  onClose,
  onSubmit,
  organizations,
  orgsLoading,
  preselectedOrgId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Get all selector experiences from Redux state for validation
  const { selectorExperiences } = useSelector(
    (state: RootState) => state.selectorExperiences
  );
  
  const [organizationId, setOrganizationId] = useState(preselectedOrgId || "");
  const [selectorId, setSelectorId] = useState("");
  const [preSelectionCopy, setPreSelectionCopy] = useState("");
  const [selectorTitle, setSelectorTitle] = useState("");
  const [selectorSubtitle, setSelectorSubtitle] = useState("");
  const [subClientModeEnabled, setSubClientModeEnabled] = useState(false);
  const [entries, setEntries] = useState<ExperienceEntry[]>([]);
  
  const [resLinks, setResLinks] = useState<ResLink[]>([]);
  const [loadingResLinks, setLoadingResLinks] = useState(false);
  const [resLinksError, setResLinksError] = useState<string | null>(null);
  const [selectorIdError, setSelectorIdError] = useState<string | null>(null);
  const [checkingSelectorId, setCheckingSelectorId] = useState(false);

  // Fetch ResLinks when organization changes
  useEffect(() => {
    if (organizationId) {
      setLoadingResLinks(true);
      setResLinksError(null);
      dispatch(fetchOrganizationResLinks(organizationId))
        .unwrap()
        .then((links) => {
          setResLinks(links);
          setResLinksError(null);
        })
        .catch((error: any) => {
          console.error("Failed to fetch ResLinks:", error);
          let errorMessage = "Failed to load resources";
          if (error.status === 403) {
            errorMessage = "You don't have permission to view resources for this organization";
          } else if (error.message) {
            errorMessage = error.message;
          }
          setResLinksError(errorMessage);
          toast.error(errorMessage);
          setResLinks([]);
        })
        .finally(() => {
          setLoadingResLinks(false);
        });
    } else {
      setResLinks([]);
      setResLinksError(null);
    }
  }, [organizationId, dispatch]);

  // Real-time selector ID validation with debounce (client-side check against loaded data)
  useEffect(() => {
    if (!selectorId) {
      setSelectorIdError(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      setCheckingSelectorId(true);
      
      // Check if selector_id already exists in any loaded selector experience
      const existingSelector = selectorExperiences.find(
        (se) => se.selector_id.toLowerCase() === selectorId.toLowerCase()
      );
      
      if (existingSelector) {
        setSelectorIdError(`Selector ID "${selectorId}" is already in use`);
      } else {
        setSelectorIdError(null);
      }
      
      setCheckingSelectorId(false);
    }, 300); // 300ms debounce for client-side check

    return () => clearTimeout(timeoutId);
  }, [selectorId, selectorExperiences]);

  const handleAddEntry = () => {
    setEntries([
      ...entries,
      { title: "", subtitle: null, res_link_params_id: "" },
    ]);
  };

  const handleRemoveEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleEntryChange = (
    index: number,
    field: keyof ExperienceEntry,
    value: string | null
  ) => {
    const newEntries = [...entries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value,
    };
    setEntries(newEntries);
  };

  const handleResLinkSelect = (index: number, value: ResLink | string | null) => {
    if (value) {
      const newEntries = [...entries];
      newEntries[index] = {
        ...newEntries[index],
        res_link_params_id: typeof value === 'string' ? value : value.id,
      };
      setEntries(newEntries);
    }
  };

  const handleSubmit = () => {
    if (!selectorId || !organizationId) {
      toast.error("Selector ID and Organization are required");
      return;
    }

    if (selectorIdError) {
      toast.error("Please fix the selector ID error before submitting");
      return;
    }

    onSubmit({
      organization_id: organizationId,
      id: selectorId,
      pre_selection_copy: preSelectionCopy || null,
      selector_title: selectorTitle || null,
      selector_subtitle: selectorSubtitle || null,
      sub_client_mode_enabled: subClientModeEnabled,
      entries,
    });
  };

  const handleClose = () => {
    // Reset form
    setOrganizationId(preselectedOrgId || "");
    setSelectorId("");
    setPreSelectionCopy("");
    setSelectorTitle("");
    setSelectorSubtitle("");
    setSubClientModeEnabled(false);
    setEntries([]);
    setSelectorIdError(null);
    onClose();
  };

  const getResLinkDisplayName = (option: ResLink | string) => {
    if (typeof option === 'string') {
      return option;
    }
    if (option.display_name) {
      return `${option.display_name} (${option.id})`;
    }
    return option.id;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Selector Experience</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Create a selector experience. You can add entries with ResLinks from the selected organization.
        </Typography>
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="organization-select-label">Organization *</InputLabel>
          <Select
            labelId="organization-select-label"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            label="Organization *"
            disabled={!!preselectedOrgId}
            required
          >
            <MenuItem value="">
              <em>Select Organization</em>
            </MenuItem>
            {orgsLoading ? (
              <MenuItem disabled>Loading organizations...</MenuItem>
            ) : (
              organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))
            )}
          </Select>
          {preselectedOrgId && (
            <FormHelperText>
              Organization is pre-selected based on current context
            </FormHelperText>
          )}
        </FormControl>

        <TextField
          label="Selector ID"
          value={selectorId}
          onChange={(e) => setSelectorId(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={!!selectorIdError}
          helperText={
            selectorIdError ||
            "Globally unique selector identifier (e.g., 'solana', 'doctor-testbed'). ⚠️ This cannot be changed after creation."
          }
          placeholder="e.g., my-selector"
          InputProps={{
            endAdornment: checkingSelectorId ? (
              <CircularProgress size={20} />
            ) : null,
          }}
        />

        <TextField
          label="Pre-Selection Copy"
          value={preSelectionCopy}
          onChange={(e) => setPreSelectionCopy(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={2}
          helperText="Text shown before selector options. Leave empty to skip the initial screen and go directly to selector options."
          placeholder="e.g., To speak with our assistant, we need to collect some information first."
        />

        <TextField
          label="Selector Title"
          value={selectorTitle}
          onChange={(e) => setSelectorTitle(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Optional: Main title of the selector"
          placeholder="e.g., What brings you to us?"
        />

        <TextField
          label="Selector Subtitle"
          value={selectorSubtitle}
          onChange={(e) => setSelectorSubtitle(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={2}
          helperText="Optional: Subtitle/description"
          placeholder="e.g., Please choose the option that best describes your reason for reaching out."
        />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={subClientModeEnabled}
                onChange={(e) => setSubClientModeEnabled(e.target.checked)}
              />
            }
            label="Sub-Client Mode Enabled"
          />
          <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
            Enable sub-client mode for this selector experience
          </Typography>
        </Box>

        {/* Entries Section */}
        <Box sx={{ mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Entries</Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              size="small"
              disabled={!organizationId}
            >
              Add Entry
            </Button>
          </Box>

          {!organizationId && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>
              Select an organization first to add entries
            </Typography>
          )}

          {resLinksError && (
            <Typography variant="body2" color="error" sx={{ fontStyle: "italic", mb: 2 }}>
              ⚠️ {resLinksError}
            </Typography>
          )}

          {entries.length > 0 && (
            <List>
              {entries.map((entry, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      display: "block",
                      backgroundColor: "#fafafa",
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Entry {index + 1}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveEntry(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <TextField
                      label="Title"
                      value={entry.title}
                      onChange={(e) => handleEntryChange(index, "title", e.target.value)}
                      fullWidth
                      margin="dense"
                      required
                    />

                    <TextField
                      label="Subtitle"
                      value={entry.subtitle || ""}
                      onChange={(e) => handleEntryChange(index, "subtitle", e.target.value || null)}
                      fullWidth
                      margin="dense"
                    />

                    {/* ResLink Picker with Autocomplete */}
                    <Autocomplete
                      options={resLinks}
                      getOptionLabel={(option) => getResLinkDisplayName(option)}
                      loading={loadingResLinks}
                      value={resLinks.find((rl) => rl.id === entry.res_link_params_id) || null}
                      onChange={(_, newValue) => handleResLinkSelect(index, newValue)}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="ResLink"
                          margin="dense"
                          required
                          helperText="Select a ResLink or type the ID directly"
                          value={entry.res_link_params_id}
                          onChange={(e) =>
                            handleEntryChange(index, "res_link_params_id", e.target.value)
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingResLinks ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </ListItem>
                  {index < entries.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={!selectorId || !organizationId || !!selectorIdError || checkingSelectorId}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSelectorExperienceDialog;
